import type { ForwardedRef } from 'react';
import { Fragment, forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { BillingFrequency, FrequencyTotals } from '@proposify/pricing-engine';
import type { RootState } from 'components/editor/grid/reduxStore/Store';
import { useAppDispatch } from 'components/editor/grid/reduxStore/Store';
import { selectContentTable, setSelectedRow } from 'components/editor/grid/reduxStore/editorSlice';
import { setActiveSidePanel } from '../../../../grid/reduxStore/blockStyleSettingsSlice';

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import { Editor } from '@tiptap/react';

import PricingTableTotalFooterRow from './PricingTableTotalFooterRow';
import ToggledInput from './ToggledInput';
import { FooterTotalRowPanel } from '../components/DiscountPanel/FooterTotalRowPanel';
import type { DiscountType } from '../components/DiscountPanel/types';
import { stripHtmlTags } from '../components/CustomTableCell';

import { updateTotalRowsWithSubtotalCalculation } from '../tableUtils/updateTotalRowsWithSubtotalCalculation';
import { setOfRowsForPricingTable, setOfRowsForPricingTableWithReferences } from '../defaultData';
import { buildFooterRowPairUpdate, discountTypeToValueFormatter, valueFormatterToDiscountType } from '../components/DiscountPanel/helpers';
import { isTaxOrDiscountRow } from '../../../../../../muiTheme/dataGridUtils';
import getTotalFooterRowValue from './getTotalFooterRowValue';
import { getFrequencyFooterData } from './frequencyFooterRows';
import { getFooterCombinedTotals } from './getFooterCombinedTotals';
import { debounce } from 'lodash';

import { TableSettingsTypes } from '../../../../SidePanel/content/TableSettings/types';
import type { TableType } from '../../../../grid/reduxStore/table.types';
import { TotalRowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { SidePanelProviderContext } from '../../../../SidePanel/content';

import { ActiveEditorContext } from '../../../TiptapEditor/ActiveEditorContext.tsx';
import useFeatureFlag from '../../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../../utils/featureFlags.ts';
import { useTableContentUndoRedo } from '../../../../hooks/useTableContentUndoRedo.ts';
import { TableContextMenuButton } from '../components/TableContextMenuButton.tsx';
import { useTableRowManipulation } from '../../../../SidePanel/content/TableSettings/RowSettings/useTableRowManipulation';
import { useTableTotals } from '../useTableTotals';

type PricingTableTotalsFooterType = {
  isEditState?: boolean;
  sectionId: string;
  blockId: string;
  sumOfSubtotalColumn: number;
  onMenuOpen: (anchorEl: HTMLElement) => void;
  frequencyTotals?: FrequencyTotals | null;
  presentFrequencies?: BillingFrequency[];
  grandTotal?: string;
};

export const FORMATTER_KEY = 'price';
export const TITLE_KEY = 'title';

export const FooterWrapper = styled(Box)({
  overflow: 'hidden',
  borderRadius: 0,
  borderColor: 'transparent',
  width: '100%',
});

export const PricingTableTotalsFooter = forwardRef(
  (
    {
      isEditState = false,
      sectionId,
      blockId,
      sumOfSubtotalColumn,
      onMenuOpen,
      frequencyTotals,
      presentFrequencies,
      grandTotal,
    }: PricingTableTotalsFooterType,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { setActiveEditor } = useContext(ActiveEditorContext);
    const { setToggledTableSettingsPanel } = useContext(SidePanelProviderContext);
    const { activeSidePanel } = useSelector((state: RootState) => state.blockStyleSettings);
    const activeCurrencySymbol = useSelector((state: RootState) => state?.documentSettings.currencySettings?.symbol);
    const featureFlags = useFeatureFlag([FeatureFlags.currencySettings]);
    const isCurrencySettingsEnabled = featureFlags[FeatureFlags.currencySettings];
    const isActiveFooterPanel = activeSidePanel === TableSettingsTypes.TABLE_FOOTER_ROWS;
    const { handleTableContentChanged } = useTableContentUndoRedo();
    const { updateTableNonFooterRows } = useTableRowManipulation();
    const getTableTotals = useTableTotals();
    const tableData = useSelector((state: RootState) => selectContentTable(state, sectionId, blockId)) || undefined;
    const { metadata, selectedRow, totalRows } = useSelector((state: RootState) => selectContentTable(state, sectionId, blockId));
    const rowsForPricingTable = isCurrencySettingsEnabled ? setOfRowsForPricingTableWithReferences : setOfRowsForPricingTable;
    const totalTableRows = totalRows || rowsForPricingTable;
    const selectedRowId = selectedRow?.id;
    const inputRefs = useRef({});
    const updatedRows = updateTotalRowsWithSubtotalCalculation(totalTableRows, sumOfSubtotalColumn);
    const { toggledTableSettingsPanel } = useContext(SidePanelProviderContext);

    const frequencyFooter = useMemo(
      () => getFrequencyFooterData(presentFrequencies ?? [], frequencyTotals ?? null),
      [presentFrequencies, frequencyTotals]
    );
    const showFrequencySubtotals = frequencyFooter.hasNonOneTime;

    // null when the table is all-one-time — consumers then fall back to the
    // per-row float pipeline.
    const combinedTotals = useMemo(
      () => (showFrequencySubtotals && grandTotal !== undefined ? getFooterCombinedTotals(grandTotal, totalTableRows) : null),
      [showFrequencySubtotals, grandTotal, totalTableRows]
    );

    const [clickedCell, setClickedCell] = useState<string | null>(null);
    const [footerPanelAnchor, setFooterPanelAnchor] = useState<{ anchorEl: HTMLElement; rowId: string } | null>(null);
    const handleTableContentChangedRef = useRef(handleTableContentChanged);
    handleTableContentChangedRef.current = handleTableContentChanged;
    const debouncedSave = useMemo(
      () =>
        debounce((previousData: TableType, newData: TableType) => {
          handleTableContentChangedRef.current(blockId, sectionId, previousData, newData);
        }, 300),
      [blockId, sectionId]
    );

    const memoizedRows = useMemo(() => {
      return updatedRows?.map((row) => {
        const { calculateBaseValue } = isCurrencySettingsEnabled
          ? getTotalFooterRowValue(row, isCurrencySettingsEnabled, activeCurrencySymbol)
          : getTotalFooterRowValue(row);
        const { rowType, subtotal, id } = row;
        const rowValue = rowType === TotalRowTypesIdentifier.SUBTOTAL ? sumOfSubtotalColumn : subtotal;
        const isRowSelected = id === selectedRowId;

        return {
          row,
          renderedProps: {
            calculateBaseValue,
            rowValue,
            isRowSelected,
          },
        };
      });
    }, [updatedRows, selectedRowId, isEditState, sumOfSubtotalColumn]);

    const handleCellClick = (e, row, cellType) => {
      if (!isEditState) return setClickedCell(null);
      if (cellType === FORMATTER_KEY) {
        setActiveEditor(null);
      }
      setClickedCell(`${row.id}-${cellType}`);
    };

    const handleRowClick = (e, row) => {
      const isInput = e.target instanceof HTMLInputElement || (Editor != null && e.target instanceof Editor);
      const isActiveRow = selectedRow?.id === row.id;
      if ((isInput && isActiveRow) || !isEditState) return;

      if (row.id !== selectedRow?.id) {
        dispatch(setSelectedRow({ sectionId, blockId, row }));
      }
    };

    const buildTotalsTableData = (nextTotalRows: TableType['totalRows']): TableType => {
      const recalculated = updateTotalRowsWithSubtotalCalculation(nextTotalRows ?? [], sumOfSubtotalColumn);
      const tableTotal = recalculated[recalculated.length - 1]?.subtotal;

      return {
        ...tableData,
        totalRows: nextTotalRows,
        metadata: {
          ...metadata,
          tableTotal,
        },
      };
    };

    const handleInputChange = (value: string, cellKey: typeof FORMATTER_KEY | typeof TITLE_KEY) => {
      const updatedRows = totalTableRows?.map((row) =>
        row.id === selectedRow?.id ? { ...row, [cellKey === FORMATTER_KEY ? 'price' : 'title']: value } : row
      );

      debouncedSave(tableData, buildTotalsTableData(updatedRows));
    };

    const footerPanelRow = footerPanelAnchor ? updatedRows?.find((row) => row.id === footerPanelAnchor.rowId) : undefined;
    const footerPanelType: DiscountType = valueFormatterToDiscountType(
      footerPanelRow?.cellConfig?.[FORMATTER_KEY]?.valueFormatter as string | undefined
    );

    const handleFooterPanelAmountCommit = (amount: string, type: DiscountType) => {
      if (!footerPanelAnchor || !isEditState) return;
      const nextTotalRows = buildFooterRowPairUpdate(
        totalTableRows,
        footerPanelAnchor.rowId,
        amount,
        discountTypeToValueFormatter(type, Boolean(isCurrencySettingsEnabled))
      );
      const updatedTableData: TableType = { ...tableData, totalRows: nextTotalRows };
      const { rows: nextRows, metadata: nextMetadata } = getTableTotals(
        updatedTableData.rows ?? [],
        updatedTableData.columns ?? [],
        updatedTableData
      );
      debouncedSave(tableData, { ...updatedTableData, rows: nextRows, metadata: nextMetadata });
    };

    const handleFooterPanelTypeCommit = async (amount: string, type: DiscountType) => {
      if (!footerPanelAnchor || !isEditState) return;
      debouncedSave.flush();
      const nextTotalRows = buildFooterRowPairUpdate(
        totalTableRows,
        footerPanelAnchor.rowId,
        amount,
        discountTypeToValueFormatter(type, Boolean(isCurrencySettingsEnabled))
      );
      const updatedRow = nextTotalRows.find((row) => row.id === footerPanelAnchor.rowId);
      if (!updatedRow) return;
      await updateTableNonFooterRows(blockId, nextTotalRows, updatedRow);
    };

    const handleFooterPanelDelete = async () => {
      if (!footerPanelAnchor || !isEditState || !totalTableRows) return;
      const deletedRowIndex = totalTableRows.findIndex((row) => row.id === footerPanelAnchor.rowId);
      if (deletedRowIndex === -1) return;
      debouncedSave.flush();
      const nextTotalRows = totalTableRows.filter((row) => row.id !== footerPanelAnchor.rowId);
      setFooterPanelAnchor(null);
      await updateTableNonFooterRows(blockId, nextTotalRows, nextTotalRows[deletedRowIndex - 1]);
    };

    useEffect(() => {
      if (!isEditState) {
        setClickedCell(null);
        setFooterPanelAnchor(null);
      }
    }, [isEditState]);

    useEffect(() => {
      if (!isActiveFooterPanel && toggledTableSettingsPanel) {
        const isGeneralTableCellSelected = toggledTableSettingsPanel.tableApi.selectedModel.field !== undefined;
        isGeneralTableCellSelected && dispatch(setSelectedRow({ sectionId, blockId, row: null }));
        setClickedCell(null);
      }
    }, [isActiveFooterPanel, toggledTableSettingsPanel]);

    useEffect(() => {
      return () => {
        debouncedSave.cancel();
      };
    }, [debouncedSave]);

    return (
      <FooterWrapper ref={ref}>
        {memoizedRows?.map(({ row, renderedProps }) => {
          const { calculateBaseValue, rowValue, isRowSelected } = renderedProps;
          const { id, rowType, title } = row;
          const isRelabeledSubtotal = showFrequencySubtotals && rowType === TotalRowTypesIdentifier.SUBTOTAL;
          const combinedRowValue = combinedTotals?.rowValues[String(id)];

          const footerRow = (
            <PricingTableTotalFooterRow
              key={id}
              blockId={blockId}
              sectionId={sectionId}
              rowType={rowType as TotalRowTypesIdentifier}
              onValueCellClick={
                isEditState && isTaxOrDiscountRow(row)
                  ? (e) => setFooterPanelAnchor({ anchorEl: e.currentTarget as HTMLElement, rowId: String(id) })
                  : undefined
              }
              subtotalValue={
                isRelabeledSubtotal ? +frequencyFooter.oneTimeTotal : combinedRowValue !== undefined ? +combinedRowValue : rowValue
              }
              priceValue={calculateBaseValue}
              isRowSelected={isRowSelected}
              pricingDecimalPlaces={metadata?.pricingDecimalPlaces}
              onClick={(e) => handleRowClick(e, row)}
              activeCurrencySymbol={activeCurrencySymbol}
              calcInputComponent={false}
              titleComponent={
                isRelabeledSubtotal ? (
                  <span className="py-numeric-cell-value" data-testid="one-time-subtotal-title">
                    {t('pricing.footer.frequency_subtotal.one_time')}
                  </span>
                ) : (
                  <ToggledInput
                    displayedValue={title}
                    inputRefs={inputRefs}
                    clickedCell={clickedCell}
                    inputType="text"
                    isRowActive={isEditState && clickedCell === `${row.id}-${TITLE_KEY}`}
                    onCellClick={(e) => handleCellClick(e, row, TITLE_KEY)}
                    onInputChange={(value) => handleInputChange(value, TITLE_KEY)}
                    row={row}
                    value={title}
                  />
                )
              }
              menuTrigger={
                <TableContextMenuButton
                  onOpen={(anchorEl) => {
                    setToggledTableSettingsPanel(null);
                    dispatch(setSelectedRow({ sectionId, blockId, row }));
                    dispatch(setActiveSidePanel({ type: TableSettingsTypes.TABLE_FOOTER_ROWS }));
                    onMenuOpen(anchorEl);
                  }}
                />
              }
            />
          );

          if (!isRelabeledSubtotal) return footerRow;

          return (
            <Fragment key={id}>
              {footerRow}
              {frequencyFooter.frequencyRows.map((frequencyRow) => (
                <PricingTableTotalFooterRow
                  key={`frequency-subtotal-${frequencyRow.frequency}`}
                  rowType={TotalRowTypesIdentifier.SUBTOTAL}
                  testIdOverride={`frequency-subtotal-${frequencyRow.frequency}`}
                  subtotalValue={+frequencyRow.total}
                  priceValue=""
                  isRowSelected={false}
                  pricingDecimalPlaces={metadata?.pricingDecimalPlaces}
                  activeCurrencySymbol={activeCurrencySymbol}
                  calcInputComponent={null}
                  titleComponent={
                    <span className="py-numeric-cell-value" data-testid={`frequency-subtotal-title-${frequencyRow.frequency}`}>
                      {t(frequencyRow.labelKey, { defaultValue: frequencyRow.fallbackLabel })}
                    </span>
                  }
                />
              ))}
            </Fragment>
          );
        })}
        {footerPanelAnchor && footerPanelRow && (
          <Popover
            open
            disableRestoreFocus
            anchorEl={footerPanelAnchor.anchorEl}
            onClose={() => setFooterPanelAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            sx={{
              '& .MuiPopover-paper': {
                borderRadius: '8px',
                marginLeft: '8px',
              },
            }}
          >
            <FooterTotalRowPanel
              key={footerPanelAnchor.rowId}
              title={stripHtmlTags(footerPanelRow.title ?? '')}
              type={footerPanelType}
              amount={String(footerPanelRow.price ?? '')}
              subtotal={String(sumOfSubtotalColumn)}
              currencySymbol={activeCurrencySymbol || '$'}
              skipMaxFixedValidation={footerPanelRow.rowType === TotalRowTypesIdentifier.TAX}
              onAmountCommit={handleFooterPanelAmountCommit}
              onTypeCommit={handleFooterPanelTypeCommit}
              onDelete={handleFooterPanelDelete}
            />
          </Popover>
        )}
      </FooterWrapper>
    );
  }
);

PricingTableTotalsFooter.displayName = 'PricingTableTotalsFooter';
